/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer } from 'react'
import { useSocket } from '../context/socket'
import { getMessages, sendNotification, createConversation, seenMessage, sendMessageAsync } from '../server/server'
import { useUser } from '../context/user'
import '../styles/Conversation.css'
import { ReactComponent as ChatTransparent } from '../svg/normalSvgs/ChatTransparent.svg'
import { ReactComponent as LetterTransparent } from '../svg/normalSvgs/LetterTransparent.svg'
import { ReactComponent as CloseAye } from '../assets/svg/CloseAye.svg'
import InfiniteScroll from 'react-infinite-scroll-component'
import AutoReplyMessage from './Messages/AutoreplyMessage'
import Message from './Messages/Message'
import Spinner from './Spinner'
import useWindowFocus from 'use-window-focus'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'

export default function Conversation({ conversation, selectChatExternally, visibleChats, refreshConversationList }) {
    const { socketStates, socket, changeSocketState } = useSocket()
    const { user } = useUser()
    const [state, setState] = useReducer((oldState, newState) => ({ ...oldState, ...newState }), {
        initLoading: true,
        messages: [],
        refreshing: false,
        number_of_messages: 0,
        messageText: '',
        littleDelay: false,
        loadingMessages: true,
        conversationInfo: conversation,
    })
    const { initLoading, messages, number_of_messages, messageText, littleDelay, conversationInfo, loadingMessages } = state
    const members = conversationInfo.members
    const isGroupChat = conversationInfo.members.length > 2
    const conversation_id = conversationInfo.conversation_id
    const windowFocused = useWindowFocus()
    const { t } = useTranslation()
    useEffect(() => {
        setState({
            conversationInfo: conversation,
            messages: [],
            initLoading: true,
            loadingMessages: true,
        })
    }, [conversation])

    useEffect(() => {
        if (conversationInfo.new) {
            fetchNewConversation()
        } else {
            loadMessages(0)
        }
    }, [conversationInfo])

    const fetchNewConversation = async () => {
        const data = {
            member_list: conversationInfo.members,
            type: conversationInfo.type,
            anonymous_chat: conversation.anonymous_chat,
        }
        const resp = await createConversation(data)
        if (resp.status === 200) {
            const data = resp.data
            setState({ conversationInfo: data, initLoading: false })
            selectChatExternally(data)
        }
    }

    const loadMessages = async (number = messages.length) => {
        if (conversationInfo.new) {
            return
        }
        const resp = await getMessages({
            loaded_messages: number,
            members: members,
            conversation_id: conversation_id,
        })
        if (resp.status === 200) {
            let data = resp.data
            data.data.forEach(async (message) => {
                if (!message?.seen?.includes(user.id)) {
                    message.seen.push(user.id)
                    const resp = await seenMessage(message)
                    if (resp.status === 200) {
                        socketEmit('seen', message)
                    }
                }
            })
            let previousList = number === 0 ? [] : messages

            setState({
                messages: [...previousList, ...data.data],
                number_of_messages: data.size,
                initLoading: false,
                loadingMessages: false,
                littleDelay: data.data.length > 0,
                loadingMore: false,
            })
        }
    }

    const socketEmit = (type, message) => {
        for (const memberId of members) {
            if (memberId === user.id) continue
            socket.emit(type, {
                username: memberId,
                message: message,
            })
        }
    }

    const handleNewSeen = async (index) => {
        let _socket = { ...socketStates }
        const message = _socket.seenList.splice(index, 1)[0]
        const message_index = messages.findIndex((e) => e.id === message.id)
        let _messages = messages
        _messages[message_index] = message
        setState({ messages: _messages })
        changeSocketState(_socket)
    }

    useEffect(() => {
        const messageIndex = socketStates?.messages?.findIndex((e) => e.conversation_id === conversation_id)
        if (messageIndex > -1) {
            let _socket = { ...socketStates }
            const message = _socket.messages.splice(messageIndex, 1)[0]
            addNewMessage(message)
            changeSocketState(_socket)
        }
        const seenIndex = socketStates?.seenList?.findIndex(() => conversation_id)
        if (seenIndex > -1 && windowFocused) {
            handleNewSeen(seenIndex)
        }
    }, [socketStates, windowFocused])

    const addNewMessage = async (message) => {
        const exist = messages.filter((m) => m.id === message.id).length
        if (exist > 0) return // Remove duplicates
        if (!message?.seen?.includes(user.id)) {
            message.seen.push(user.id)
            setState({
                messages: [message, ...messages],
                number_of_messages: number_of_messages + 1,
            })
            const resp = await seenMessage(message)
            if (resp.status === 200) {
                socketEmit('seen', message)
            }
        } else {
            setState({
                messages: [message, ...messages],
                number_of_messages: number_of_messages + 1,
            })
        }
    }

    const sendMessage = async (e) => {
        e.preventDefault()
        if (!messageText) return
        const message = {
            message_body: messageText.trim(),
            conversation_id: conversation_id,
            user_id: user.id,
            seen: [user.id],
            created_date: Date.now(),
        }
        setState({ messageText: '' })
        const resp = await sendMessageAsync(message)
        if (resp.status === 200) {
            setState({
                messageText: '',
                messages: [resp.data, ...messages],
                number_of_messages: number_of_messages + 1,
            })
            socketEmit('message', resp.data)
            for (const memberId of members) {
                if (memberId === user.id) continue
                sendNotification({
                    data: {
                        recipient_id: memberId,
                        message: resp.data,
                        type: 'message',
                        isSos: conversationInfo.type === 'sos',
                    },
                })
                refreshConversationList()
            }
            if (!littleDelay)
                setTimeout(() => {
                    setState({ littleDelay: true })
                }, 1000)
        }
    }

    const showAutoReply =
        !conversation?.anonymous_chat && messages.map((e) => e.user_id === user.id).every((e) => e) && messages.length > 0 && !initLoading && littleDelay && messages.length < 5
    const conversationName = user.role === 0 ? conversationInfo?.label_for_student : conversationInfo?.label_for_teacher
    const names = conversationInfo?.names
    const imageBackground = conversationInfo?.color

    return (
        <div className="conversation-container">
            <div className="conversation-header shadow-custom-soft" style={{ backgroundColor: visibleChats ? '#ffffff' : '#3c879435' }}>
                {!initLoading && (
                    <>
                        <div className={`chat-icon-circle ${i18n.language === 'ar' ? 'ar-transform' : ''}`} style={{ backgroundColor: imageBackground }}>
                            <ChatTransparent style={{ color: conversation.conversation_type === 'teacher' ? 'var(--deep-ocean)' : '#F8F8FB' }} />
                        </div>
                        <div>
                            <div className="conversation-type" style={{ color: visibleChats ? 'var(--deep-ocean)' : '#fff' }}>
                                {t(conversationName)}
                            </div>
                        </div>
                        <div className="conversation-close-aye">
                            <CloseAye />
                        </div>
                    </>
                )}
            </div>
            <div
                id="scrollableDiv"
                style={{
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                }}
                className="conversation-main scrollbar-none"
            >
                <InfiniteScroll
                    dataLength={messages.length}
                    next={loadMessages}
                    style={{ display: 'flex', flexDirection: 'column-reverse' }} //To put endMessage and loader to the top.
                    inverse={true} //
                    hasMore={messages.length !== number_of_messages}
                    loader={<Spinner />}
                    scrollableTarget="scrollableDiv"
                >
                    {loadingMessages && (
                        <div
                            style={{
                                flex: 1,
                                justifyContent: 'center',
                                alignContent: 'center',
                                alignItems: 'center',
                                minHeight: 'calc(100vh - 200px)',
                                display: 'flex',
                            }}
                        >
                            <Spinner />
                        </div>
                    )}
                    {!loadingMessages && (
                        <>
                            <div className="conversation-grow"></div>
                            <div className="conversation-messages">
                                {showAutoReply && <AutoReplyMessage />}
                                {messages.map((message, index) => (
                                    <Message
                                        key={message.id}
                                        message={message}
                                        user={user}
                                        messages={messages}
                                        names={names}
                                        members={members}
                                        isGroupChat={isGroupChat}
                                        index={index}
                                        visibleChats={visibleChats}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </InfiniteScroll>
            </div>
            {conversation.anonymous_chat && <div className="anonymous-info-text">{t('This message will be sent anonymously to your teacher, ensuring your privacy')}</div>}
            <form onSubmit={sendMessage}>
                <div className="new-message-input-box ">
                    <div className="new-message-input shadow-custom-soft">
                        <input type="text" value={messageText} placeholder={t('writeYourMessageHere')} onChange={(e) => setState({ messageText: e.target.value })} />
                        <div className="new-message-input-icon" onClick={sendMessage}>
                            <LetterTransparent />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}
